<template>
    <modal size="md" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title" v-if="job">Edit Job Information</h3>
            <h3 class="modal-title" v-else>Add Job Information</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Employee: </span>{{ employee.full_name }}</div>
                    <div><span class="text-muted">Company: </span>{{ employee.company.name }}</div>
                    <div v-if="job"><span class="text-muted">Position: </span>{{ selectedPosition?.terminal_value }}</div>
                </div>
            </div>

            <div class="form-row" v-if="!job">
                <div class="col">
                    <form-input label="Position" v-model="formData.position" :errors="errors.position" type="select" :options="positionTypeOptions" :noblank="true" @update:model-value="onPositionChange"/>
                </div>
            </div>

            <div class="form-row">
                <div class="col">
                    <form-input label="Pay Type" v-model="formData.pay_type" :errors="errors.pay_type" type="select" :options="payTypeOptions" :noblank="true" />
                </div>
            </div>

            <div class="form-row">
                <div class="col col-6" v-if="formData.pay_type == 'H'">
                    <form-input label="Hourly Rate" helper-text="per pay hour" v-model="formData.hourly_rate" :errors="errors.hourly_rate" type="currency" />
                </div>

                <div class="col col-6"  v-if="formData.pay_type == 'H' && selectedPosition && selectedPosition.is_regularly_tipped && client.uses_explicit_tip_credit">
                    <form-input label="Tip Credit" v-model="formData.tip_credit_rate" :errors="errors.tip_credit_rate" type="currency" />
                </div>

                <div class="col col-6" v-if="['S', 'SNE'].includes(formData.pay_type)">
                    <form-input label="Salary Rate" helper-text="per pay period" v-model="formData.salary_rate" :errors="errors.salary_rate" type="currency" />
                </div>

                <div class="col col-6" v-if="['S', 'SNE'].includes(formData.pay_type)">
                    <form-input label="Default Hours" v-model="formData.default_hours" :errors="errors.default_hours" type="integer" />
                </div>

                <div class="col col-6" v-if="formData.pay_type == 'P'">
                    <form-input label="Rate" helper-text="per unit" v-model="formData.unit_rate" :errors="errors.unit_rate" type="currency" />
                </div>

            </div>

            <h5 class="table-section-title pt-2">Additional Compensation</h5>
            <hr class="mt-1 mb-2"/>

            <template v-if="ownCompensationIndexes.length || positionDefaultEarningTypes.length">
                <div class="form-row">
                    <div class="col col-7">
                        <h5 class="text-muted">Earning</h5>
                    </div>

                    <div class="col col-4">
                        <h5 class="text-muted">Rate</h5>
                    </div>

                    <div class="col col-1">
                    </div>
                </div>

                <hr class="mt-1 mb-1"/>

                <!-- Position defaults. A rate entered on one is saved as the job's additional
                compensation for that earning. -->
                <div class="form-row additional-compensation-row" v-for="cet in positionDefaultEarningTypes" :key="`default-${cet.id}`">
                    <div class="col col-7">
                        {{ cet.name }}
                    </div>

                    <div class="col col-4">
                        <form-input
                            v-if="hasRateInput(cet)"
                            :postfix="ratePostfix(cet)"
                            label=""
                            :model-value="defaultRate(cet)"
                            @update:model-value="setDefaultRate(cet, $event)"
                            type="currency"
                        />
                        <div v-else-if="calculatedRateLabelFor(cet)" class="text-muted">
                            <span v-if="calculatedRateFor(cet) !== null">${{ calculatedRateFor(cet) }}</span>
                            <span v-else>&mdash;</span>
                            <span class="text-small d-block">({{ calculatedRateLabelFor(cet) }})</span>
                        </div>
                    </div>

                    <div class="col col-1">
                    </div>
                </div>

                <!-- The job's additional compensation, except entries for a position default, which
                show on the default's row above -->
                <div class="form-row additional-compensation-row" v-for="idx in ownCompensationIndexes" :key="`ac-${idx}`">
                    <div class="col col-7">
                        <form-input
                            label=""
                            v-model="formData.additional_compensation[idx].earning_type_slug"
                            :errors="errors.additional_compensation[idx].earning_type_slug"
                            type="select"
                            :options="earningTypeOptions"
                            :noblank="true"
                        />
                    </div>

                    <div class="col col-4">
                        <!-- Base-rate earnings and time off compute their rate from the hourly rate or salary; show it instead of an input. -->
                        <form-input
                            v-if="hasRateInput(selectedEarningTypes[idx])"
                            :postfix="ratePostfix(selectedEarningTypes[idx])"
                            label=""
                            v-model="formData.additional_compensation[idx].rate"
                            :errors="errors.additional_compensation[idx].rate"
                            type="currency"
                        />
                        <div v-else-if="calculatedRateLabelFor(selectedEarningTypes[idx])" class="text-muted">
                            <span v-if="calculatedRateFor(selectedEarningTypes[idx]) !== null">${{ calculatedRateFor(selectedEarningTypes[idx]) }}</span>
                            <span v-else>&mdash;</span>
                            <span class="text-small d-block">({{ calculatedRateLabelFor(selectedEarningTypes[idx]) }})</span>
                        </div>
                    </div>

                    <div class="col col-1">
                        <button class="btn btn-link" @click.prevent="removeAdditionalCompensation(idx)"><i class="far fa-trash-alt"></i></button>
                    </div>
                </div>
            </template>

            <div class="form-row mt-2 mb-2" v-else>
                <div class="col text-center">
                    <i>This employee does not have any additional earnings.</i>
                </div>
            </div>

            <div class="form-row mb-3">
                <div class="col">
                    <button
                        class="btn btn-link px-0"
                        :class="{'disabled': earningTypeOptions.length <= ownCompensationIndexes.length}"
                        @click.prevent="addAdditionalCompensation()">
                        <i class="far fa-plus"></i> Add Earning
                    </button>
                </div>
            </div>

            <div class="form-row">
                <div class="col">
                    <form-input
                        label="Effective Pay Period"
                        v-model="effectiveDateSelection"
                        type="select"
                        :options="effectiveDateOptions"
                        :noblank="true"
                        :errors="effectiveDateSelection === 'custom' ? [] : errors.effective_on"
                    />
                </div>
            </div>
            <div class="form-row" v-if="effectiveDateSelection === 'custom'">
                <div class="col col-6">
                    <form-input label="Effective Date" v-model="formData.effective_on" :errors="errors.effective_on" type="date" />
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import moment from 'moment-timezone'

// EarningType.TIME_OFF_USAGE_DESIGNATIONS: paid at the job's hourly rate, or the salary's on a
// salaried job (PaygroupRun._earning_rate_for_job)
const TIME_OFF_USAGE_DESIGNATIONS = ['vacation-usage', 'sick-usage', 'other-time-off-usage']

export default {
    props: ['client', 'employee', 'job', 'futureMutations', 'clientEarningTypes', 'positions', ],
    mixins: [FormMixin, ModalMixin],
    components: {},
    emits: ['updated', 'close', ],
    data() {
        const dateValidation = {dateOnly: true}

        if (this.job) {
            if (this.job.effective_on) {
                dateValidation.earliest = moment(this.job.effective_on).format('YYYY-MM-DD')
            }
            else {
                dateValidation.earliest = moment().tz('America/New_York').format('YYYY-MM-DD')
            }
        }

        const data = {
            formData: this.makeFormData(),
            formRules: {
                pay_type: {presence: {allowEmpty: false}, },
                effective_on: {presence: {allowEmpty: false}, date: dateValidation},
            },
            formLabels: {
            },
        }

        data.errors = {
            additional_compensation: []
        }

        // effectiveDateSelection: a pay period start date, or 'custom' for the free-form date input.
        data.payPeriodOptions = []
        data.effectiveDateSelection = 'custom'

        if (this.job) {
            data.formRules.position = {presence: {allowEmpty: false}}

            this.job.additional_compensation.forEach(ac => {
                data.errors.additional_compensation.push({})
            })
        }

        return data
    },
    watch: {
        'formData.pay_type'() {
            if (this.formData.pay_type == 'H') {
                this.formData.default_hours = ''
            }
            else {
                this.formData.tip_credit_rate = ''
            }
        },
        effectiveDateSelection(value) {
            // Picking a period sets effective_on to its start date; 'custom' leaves it editable.
            if (value !== 'custom') {
                this.formData.effective_on = moment(value).format('MM/DD/YYYY')
            }
        },
    },
    mounted() {
        this.fetchPayPeriodOptions()
    },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.employee.company_id)
        },
        selectedPosition() {
            let positionId = null

            if (this.job?.position) {
                if (this.job.position.id) {
                    return this.job.position
                } else {
                    positionId = this.job.position_id
                }
            } else if (this.formData.position) {
                positionId = this.formData.position
            }

            if (positionId) {
                return this.positions.find(p => p.id == positionId)
            }
            return null
        },
        positionTypeOptions() {
            const result = []

            const existingsPositionIds = this.employee.jobs.map(j => j.is_active && j.position_id)
            existingsPositionIds.push(...this.futureMutations.map(m => !m.is_applied && m.position_id))

            this.positions.forEach(p => {
                if (p.company_id == this.employee.company_id) {
                    if (!existingsPositionIds.includes(p.id)) {
                        result.push({text: p.terminal_value, value: p.id})
                    }
                }
            })

            result.sort((a, b) => a.text > b.text ? 1 : -1)

            return result
        },
        payTypeOptions() {
            return [
                {text: 'Hourly', value: 'H'},
                {text: 'Salary', value: 'S'},
                {text: 'Piecemeal', value: 'P'},
            ]
        },
        earningTypeOptions() {
            // Leaves out the position's defaults, which have their own rows
            const result = []
            this.clientEarningTypes.filter(cet => cet.company_id == this.employee.company_id).forEach(cet => {
                if (['regular', 'overtime', 'tips'].includes(cet.earning_type.slug)) {return}
                if (this.defaultEarningTypeSlugs.includes(cet.slug)) {return}
                result.push({text: cet.name, value: cet.slug})
            })

            result.sort((a, b) => a.text > b.text ? 1 : -1)

            return result
        },
        isSalaried() {
            return ['S', 'SNE'].includes(this.formData.pay_type)
        },
        positionDefaultEarningTypes() {
            // The earnings the paygrid adds for every job at the position. Salaried jobs don't
            // get hourly or per-unit ones apart from time off (PaygroupRun._build_earnings_for_job).
            const defaultIds = this.selectedPosition?.default_company_earning_type_ids || []
            return this.clientEarningTypes
                .filter(cet => defaultIds.includes(cet.id))
                .filter(cet => !['regular', 'overtime'].includes(cet.earning_type.slug))
                .filter(cet => !(this.isSalaried && ['hourly', 'per-unit'].includes(cet.type) && !this.isTimeOff(cet)))
                .sort((a, b) => this.compareEarningTypes(a, b))
        },
        defaultEarningTypeSlugs() {
            return this.positionDefaultEarningTypes.map(cet => cet.slug)
        },
        ownCompensationIndexes() {
            // Indexes of the additional compensation entries that aren't for a position default,
            // in the order added. An entry for a default only holds its rate and shows on its row.
            const indexes = []
            this.formData.additional_compensation.forEach((ac, idx) => {
                if (!this.defaultEarningTypeSlugs.includes(ac.earning_type_slug)) {
                    indexes.push(idx)
                }
            })
            return indexes
        },
        selectedEarningTypes() {
            const result = []
            this.formData.additional_compensation.forEach(ac => {
                const cet = this.clientEarningTypes.find(cet =>
                    cet.company_id == this.employee.company_id && cet.slug == ac.earning_type_slug
                )
                result.push(cet ? cet : {})
            })

            return result
        },
        currentPayPeriod() {
            const today = moment().tz('America/New_York').format('YYYY-MM-DD')
            return this.payPeriodOptions.find(pp =>
                pp.pay_period_start_date <= today && pp.pay_period_end_date >= today
            ) || null
        },
        effectiveDateOptions() {
            const options = this.payPeriodOptions.map(pp => ({
                text: this.payPeriodLabel(pp),
                value: pp.pay_period_start_date,
            }))
            options.push({text: 'Custom', value: 'custom'})
            return options
        },
    },
    methods: {
        makeFormData() {
            const data = {
                position: this.job ? this.job.position_id : '',
                pay_type: this.job ? this.job.pay_type : 'H',
                hourly_rate: this.job ? this.job.hourly_rate : '',
                tip_credit_rate: this.job ? this.job.tip_credit_rate : '',
                salary_rate: this.job ? this.job.salary_rate : '',
                unit_rate: this.job ? this.job.unit_rate : '',
                default_hours: this.job ? this.job.default_hours : '',
                additional_compensation: this.job ? JSON.parse(JSON.stringify(this.job.additional_compensation)) : [],
                // Stored as MM/DD/YYYY to match the date input's format and the form's date validator.
                effective_on: this.job?.effective_on
                    ? moment(this.job.effective_on, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('MM/DD/YYYY')
                    : moment().format('MM/DD/YYYY'),
            }

            return data
        },
        getFormRules() {
            const rules = JSON.parse(JSON.stringify(this.formRules))

            if (!this.job) {
                rules.position = {presence: {allowEmpty: false}, }
            }

            // Period options can start in the past, so the not-before-today rule only
            // applies to custom dates.
            if (this.effectiveDateSelection !== 'custom') {
                rules.effective_on = {presence: {allowEmpty: false}, date: {dateOnly: true}}
            }

            if (['S', 'SNE'].includes(this.formData.pay_type)) {
                rules.default_hours = {presence: {allowEmpty: false}, }
                rules.salary_rate = {presence: {allowEmpty: false}, }
            }

            if (['H'].includes(this.formData.pay_type)) {
                rules.hourly_rate = {presence: {allowEmpty: false}, }
            }

            if (['P'].includes(this.formData.pay_type)) {
                rules.unit_rate = {presence: {allowEmpty: false}, }
            }

            return rules
        },
        validate() {
            let isValid = FormMixin.methods.validate.call(this)

            this.errors.additional_compensation = []
            this.formData.additional_compensation.forEach((ac, idx) => {
                this.errors.additional_compensation[idx] = {}

                if (!ac.earning_type_slug) {
                    isValid = false
                    this.errors.additional_compensation[idx].earning_type_slug = ["Earning type can't be blank."]
                }
                else if (this.hasRateInput(this.selectedEarningTypes[idx])) {
                    if (!ac.rate) {
                        isValid = false
                        this.errors.additional_compensation[idx].rate = ["Rate can't be blank."]
                    }
                }
            })

            return isValid
        },
        addAdditionalCompensation() {
            this.formData.additional_compensation.push({
                earning_type_slug: '',
            })

            if (!this.errors.additional_compensation) {
                this.errors.additional_compensation = []
            }
            this.errors.additional_compensation.push({})
        },
        removeAdditionalCompensation(idx) {
            this.formData.additional_compensation.splice(idx, 1)
            this.errors.additional_compensation.splice(idx, 1)
        },
        isTimeOff(cet) {
            return TIME_OFF_USAGE_DESIGNATIONS.includes(cet?.earning_type?.designation)
        },
        compareEarningTypes(a, b) {
            // Sort priority, then name
            const priorityA = a.sort_priority ?? Number.MAX_SAFE_INTEGER
            const priorityB = b.sort_priority ?? Number.MAX_SAFE_INTEGER
            if (priorityA !== priorityB) {
                return priorityA - priorityB
            }
            return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
        },
        ratePostfix(cet) {
            return cet?.type === 'per-unit' ? '/unit' : '/hr'
        },
        hasRateInput(cet) {
            // Rated earnings take a rate, unless it's computed from the hourly rate
            return ['per-unit', 'hourly'].includes(cet?.type) && !cet.is_tied_to_base_rate && !this.isTimeOff(cet)
        },
        calculatedRateFor(cet) {
            // Time off on a salaried job is paid at the salary's hourly rate
            // (PaygroupRun._earning_rate_for_job)
            if (this.isTimeOff(cet) && this.isSalaried) {
                const salaryRate = parseFloat(this.formData.salary_rate)
                const defaultHours = parseFloat(this.formData.default_hours)
                return !isNaN(salaryRate) && defaultHours ? (salaryRate / defaultHours).toFixed(2) : null
            }
            if (cet?.is_tied_to_base_rate) {
                return this.baseRateFor(cet)
            }
            const hourlyRate = parseFloat(this.formData.hourly_rate)
            return this.isTimeOff(cet) && !isNaN(hourlyRate) ? hourlyRate.toFixed(2) : null
        },
        calculatedRateLabelFor(cet) {
            if (this.isTimeOff(cet) && this.isSalaried) {
                return 'Salary ÷ Default Hours'
            }
            if (cet?.is_tied_to_base_rate) {
                return this.baseRateLabelFor(cet)
            }
            return this.isTimeOff(cet) ? 'Hourly Rate' : ''
        },
        defaultRate(cet) {
            const ac = this.formData.additional_compensation.find(ac => ac.earning_type_slug === cet.slug)
            return ac ? ac.rate : ''
        },
        setDefaultRate(cet, rate) {
            // Entering a rate adds or updates an additional compensation entry for the default;
            // clearing it removes the entry, so the rate is entered on the paygrid again
            const idx = this.formData.additional_compensation.findIndex(ac => ac.earning_type_slug === cet.slug)
            if (!rate) {
                if (idx !== -1) {
                    this.removeAdditionalCompensation(idx)
                }
                return
            }
            if (idx === -1) {
                this.formData.additional_compensation.push({earning_type_slug: cet.slug, rate: rate})
                this.errors.additional_compensation.push({})
            } else {
                this.formData.additional_compensation[idx].rate = rate
            }
        },
        baseRateFor(cet) {
            // hourly * multiplier + addition, matching the backend seed; null when there
            // is no hourly rate to compute from.
            if (!cet || !cet.is_tied_to_base_rate) {
                return null
            }
            const hourlyRate = parseFloat(this.formData.hourly_rate)
            if (isNaN(hourlyRate)) {
                return null
            }
            const multiplier = parseFloat(cet.base_rate_multiplier) || 0
            const addition = parseFloat(cet.base_rate_addition) || 0
            return (hourlyRate * multiplier + addition).toFixed(2)
        },
        baseRateLabelFor(cet) {
            // e.g. "1.5x Hourly Rate + $2.00"
            if (!cet || !cet.is_tied_to_base_rate) {
                return ''
            }
            const multiplier = parseFloat(cet.base_rate_multiplier) || 0
            const addition = parseFloat(cet.base_rate_addition) || 0
            let label = `${multiplier}x Hourly Rate`
            if (addition) {
                label += ` + $${addition.toFixed(2)}`
            }
            return label
        },
        onPositionChange() {
            // The position's default earnings aren't copied into the job's additional
            // compensation: the paygrid adds them for every job at the position on its own.
            // The paygroup (and thus its pay periods) is determined by the selected position.
            this.fetchPayPeriodOptions()
        },
        fetchPayPeriodOptions() {
            const paygroupId = this.selectedPosition?.paygroup_id
            if (!paygroupId) {
                this.payPeriodOptions = []
                this.effectiveDateSelection = 'custom'
                return
            }
            return this.$api.get(`/clients/${this.client.id}/paygroups/${paygroupId}/pay-period-options`).then(resp => {
                this.payPeriodOptions = resp
                this.initEffectiveDateSelection()
            }).catch(() => {
                this.payPeriodOptions = []
                this.effectiveDateSelection = 'custom'
            })
        },
        initEffectiveDateSelection() {
            // Preselect the period matching the saved effective date, else the current
            // period, else 'custom'. The watcher then keeps effective_on in step.
            const currentIso = moment(this.formData.effective_on, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
            const match = this.payPeriodOptions.find(pp => pp.pay_period_start_date === currentIso)
            if (match) {
                this.effectiveDateSelection = match.pay_period_start_date
            }
            else if (this.currentPayPeriod) {
                this.effectiveDateSelection = this.currentPayPeriod.pay_period_start_date
            }
            else {
                this.effectiveDateSelection = 'custom'
            }
        },
        payPeriodLabel(pp) {
            return `${this.$filters.date(pp.pay_period_start_date)} - ${this.$filters.date(pp.pay_period_end_date)}`
        },
        getFormData() {
            const data = JSON.parse(JSON.stringify(this.formData))
            if (!this.client.uses_explicit_tip_credit) {
                delete data.tip_credit_rate
            }

            if (['S', 'SNE'].includes(data.pay_type)) {
                console.log(1)
                delete data.hourly_rate
                delete data.unit_rate
            }

            if (['H'].includes(data.pay_type)) {
                console.log(2)
                delete data.default_hours
                delete data.salary_rate
                delete data.unit_rate
            }

            if (['P'].includes(data.pay_type)) {
                console.log(3)
                delete data.default_hours
                delete data.salary_rate
                delete data.hourly_rate
            }

            return data
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            const data = this.getFormData()

            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/employees/${this.employee.id}/job-mutations`, data).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully saved job information.`)
                this.$emit('updated', resp)
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors

                if (!this.errors.additional_compensation) {
                    this.errors.additional_compensation = []
                }

                for (let idx = 0; idx < this.formData.additional_compensation.length; idx++) {
                    this.errors.additional_compensation[idx] = this.errors.additional_compensation[idx] || {}
                }

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        }
    }
}
</script>

<style scoped>
    /* Rows are only as tall as their fields, with the earning name centered on the rate
       input; the fields drop their usual top padding and 20px bottom margin */
    .additional-compensation-row {
        align-items: center;
        min-height: 32px;
        margin-bottom: 0.25rem;
    }

    .additional-compensation-row :deep(.mui-textfield),
    .additional-compensation-row :deep(.select2-control) {
        margin-bottom: 0;
        padding-top: 0;
    }
</style>
